import { Placement } from '@floating-ui/react'
import { MotionProps } from 'motion/react'

/**
 * Anchor the scale animation to the edge nearest the trigger, so a floating surface reads as growing out of it.
 * Keyed on the placement floating-ui resolves, so a flipped surface still grows from the right edge.
 */
export const TRANSFORM_ORIGIN_BY_PLACEMENT: Record<Placement, string> = {
    top: 'origin-bottom',
    'top-start': 'origin-bottom-left',
    'top-end': 'origin-bottom-right',
    bottom: 'origin-top',
    'bottom-start': 'origin-top-left',
    'bottom-end': 'origin-top-right',
    left: 'origin-right',
    'left-start': 'origin-top-right',
    'left-end': 'origin-bottom-right',
    right: 'origin-left',
    'right-start': 'origin-top-left',
    'right-end': 'origin-bottom-left',
}

/**
 * A front-loaded curve, not the built-in `easeOut`, which spends most of its duration crawling to the
 * finish and so reads as slower than it measures. This one covers most of the distance in the first
 * third, which is what makes the panel feel like it is already there.
 */
const ENTER_TRANSITION = { ease: [0.32, 0.72, 0, 1], duration: 0.08 } as const
const EXIT_TRANSITION = { ease: 'easeIn', duration: 0.06 } as const

/**
 * Enter and exit shared by the floating surfaces, so a popover and a hover card cannot drift apart.
 * The exit is shallower and quicker: the user has already decided to dismiss, so anything they can
 * wait through is too long.
 *
 * Spread onto a motion component inside an AnimatePresence, and spread nothing at all for reduced motion.
 */
export const FLOATING_MOTION_PROPS = {
    initial: { opacity: 0, scale: 0.95 },
    animate: { opacity: 1, scale: 1 },
    exit: { opacity: 0, scale: 0.95, transition: EXIT_TRANSITION },
    transition: ENTER_TRANSITION,
} satisfies MotionProps

/** An overlay only fades on the same timings: scaling a full-screen dim shows up as a moving edge. */
export const FLOATING_OVERLAY_MOTION_PROPS = {
    initial: { opacity: 0 },
    animate: { opacity: 1 },
    exit: { opacity: 0, transition: EXIT_TRANSITION },
    transition: ENTER_TRANSITION,
} satisfies MotionProps
